import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ConnectedEmail } from '../components/ConnectedEmail';
import { Button, Card, ErrorBox, Field, Input, PageHeader, Select, Spinner } from '../components/ui';
import { useUserId } from '../lib/auth';
import { dateTime } from '../lib/format';
import { DEAL_TYPE_LABEL } from '../lib/deals';
import { downloadCsv, downloadJson, downloadWorkbook, EXPORT_TABLES, loadEverything, type ExportTable } from '../lib/exportData';
import { supabase, unwrap, unwrapMaybe } from '../lib/supabase';

export function Settings() {
  return (
    <div className="max-w-4xl space-y-4">
      <PageHeader title="Settings" />
      <ConnectedEmail />
      <PasswordCard />
      <ExportCard />
      <StagesCard />
      <DigestCard />
      <IgnoreListCard />
    </div>
  );
}

function PasswordCard() {
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const save = useMutation({
    mutationFn: async () => {
      if (pw.length < 8) throw new Error('Use at least 8 characters.');
      if (pw !== pw2) throw new Error('The two passwords don’t match.');
      const { error } = await supabase.auth.updateUser({ password: pw });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      setPw('');
      setPw2('');
    },
  });
  return (
    <Card title="Sign-in password">
      <p className="mb-3 text-sm text-slate-600">Sign in on a new device with your email and this password. Each device stays signed in until you sign out.</p>
      <div className="flex flex-wrap items-end gap-2">
        <Field label="New password">
          <Input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} className="!w-56" />
        </Field>
        <Field label="Again">
          <Input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="!w-56" />
        </Field>
        <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()} disabled={!pw}>
          Save password
        </Button>
      </div>
      {save.isSuccess && <p className="mt-2 text-sm text-emerald-700">Password saved.</p>}
      <ErrorBox error={save.error} />
    </Card>
  );
}

function ExportCard() {
  const [status, setStatus] = useState<string | null>(null);
  const [csvTable, setCsvTable] = useState<ExportTable>('contacts');
  const run = useMutation({
    mutationFn: async (kind: 'xlsx' | 'json') => {
      const data = await loadEverything((t) => setStatus(`Reading ${t}…`));
      setStatus('Preparing file…');
      if (kind === 'xlsx') await downloadWorkbook(data);
      else await downloadJson(data);
    },
    onSettled: () => setStatus(null),
  });
  const csv = useMutation({ mutationFn: () => downloadCsv(csvTable) });
  return (
    <Card title="Export everything">
      <p className="mb-3 text-sm text-slate-600">Download all of your data. Nothing in the CRM is ever locked in.</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="primary" busy={run.isPending && run.variables === 'xlsx'} disabled={run.isPending} onClick={() => run.mutate('xlsx')}>
          <Download className="h-4 w-4" /> Excel (a sheet per table)
        </Button>
        <Button busy={run.isPending && run.variables === 'json'} disabled={run.isPending} onClick={() => run.mutate('json')}>
          <Download className="h-4 w-4" /> JSON
        </Button>
        <span className="mx-2 text-slate-300">|</span>
        <Select value={csvTable} onChange={(e) => setCsvTable(e.target.value as ExportTable)} className="!w-56">
          {EXPORT_TABLES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>
        <Button busy={csv.isPending} onClick={() => csv.mutate()}>
          CSV
        </Button>
      </div>
      {status && <p className="mt-2 text-xs text-slate-500">{status}</p>}
      <ErrorBox error={run.error ?? csv.error} />
    </Card>
  );
}

function StagesCard() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({
    queryKey: ['settings-stages'],
    queryFn: async () => unwrap(await supabase.from('deal_stages').select('id, deal_type, stage_key, label, probability, sort_order').order('deal_type').order('sort_order')),
  });
  const save = useMutation({
    mutationFn: async ({ id, probability, label }: { id: string; probability?: number; label?: string }) =>
      unwrap(await supabase.from('deal_stages').update({ ...(probability !== undefined && { probability }), ...(label !== undefined && { label }) }).eq('id', id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings-stages'] });
      qc.invalidateQueries({ queryKey: ['pipeline'] });
      qc.invalidateQueries({ queryKey: ['revenue'] });
    },
  });
  if (isLoading) return <Spinner />;
  const types = [...new Set(data.map((s) => s.deal_type))];
  return (
    <Card title="Deal stages and probabilities">
      <p className="mb-3 text-sm text-slate-600">Probabilities weight the revenue projection. Changes apply immediately to every deal in that stage.</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {types.map((t) => (
          <div key={t}>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{DEAL_TYPE_LABEL[t] ?? t}</div>
            <ul className="space-y-1">
              {data
                .filter((s) => s.deal_type === t)
                .map((s) => (
                  <li key={s.id} className="flex items-center gap-2 text-sm">
                    <Input defaultValue={s.label} onBlur={(e) => e.target.value.trim() && e.target.value !== s.label && save.mutate({ id: s.id, label: e.target.value.trim() })} />
                    <Input
                      className="!w-20 text-right"
                      defaultValue={Math.round(Number(s.probability) * 100)}
                      onBlur={(e) => {
                        const pct = Number(e.target.value);
                        if (pct >= 0 && pct <= 100 && pct / 100 !== Number(s.probability)) save.mutate({ id: s.id, probability: pct / 100 });
                      }}
                    />
                    <span className="text-slate-400">%</span>
                  </li>
                ))}
            </ul>
          </div>
        ))}
      </div>
      <ErrorBox error={save.error} />
    </Card>
  );
}

function DigestCard() {
  const qc = useQueryClient();
  const userId = useUserId();
  const { data } = useQuery({
    queryKey: ['app-settings'],
    queryFn: async () => unwrapMaybe(await supabase.from('app_settings').select('*').eq('owner_id', userId).maybeSingle()),
  });
  const save = useMutation({
    mutationFn: async (patch: { digest_time?: string; digest_weekends?: boolean; digest_enabled?: boolean }) =>
      unwrap(await supabase.from('app_settings').upsert({ owner_id: userId, ...patch }, { onConflict: 'owner_id' })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app-settings'] }),
  });
  const { data: last } = useQuery({
    queryKey: ['last-digest'],
    queryFn: async () =>
      unwrapMaybe(await supabase.from('digests').select('digest_date, sent_at, send_error').order('digest_date', { ascending: false }).limit(1).maybeSingle()),
  });
  const test = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('digest', { body: { test: true } });
      if (error) {
        const ctx = (error as { context?: Response }).context;
        const msg = ctx ? ((await ctx.json().catch(() => null)) as { error?: string } | null)?.error : null;
        throw new Error(msg ?? error.message);
      }
      return data as { to: string; subject: string };
    },
  });
  return (
    <Card title="Morning summary email">
      <p className="mb-3 text-sm text-slate-600">
        Each morning: payments to confirm, reminders due, emails from contacts you haven’t answered, deal next steps and quiet deals. It’s sent from your
        connected Outlook mailbox to yourself.
      </p>
      <div className="flex flex-wrap items-end gap-4">
        <Field label="Send at (Eastern)">
          <Input type="time" defaultValue={(data?.digest_time ?? '06:30').slice(0, 5)} onBlur={(e) => save.mutate({ digest_time: e.target.value })} className="!w-32" />
        </Field>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input type="checkbox" checked={data?.digest_weekends ?? true} onChange={(e) => save.mutate({ digest_weekends: e.target.checked })} /> Weekends too
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <input type="checkbox" checked={data?.digest_enabled ?? true} onChange={(e) => save.mutate({ digest_enabled: e.target.checked })} /> Enabled
        </label>
        <Button className="mb-1" busy={test.isPending} onClick={() => test.mutate()}>
          Send me one now
        </Button>
      </div>
      {test.data && (
        <p className="mt-2 text-sm text-emerald-700">
          Sent to {test.data.to}: “{test.data.subject}”. It should arrive within a minute.
        </p>
      )}
      {last && (
        <p className="mt-2 text-xs text-slate-500">
          {last.sent_at ? `Last morning summary sent ${dateTime(last.sent_at)}.` : last.send_error ? `The ${last.digest_date} summary failed: ${last.send_error}` : null}
        </p>
      )}
      <ErrorBox error={test.error ?? save.error} />
    </Card>
  );
}

function IgnoreListCard() {
  const qc = useQueryClient();
  const [pattern, setPattern] = useState('');
  const { data = [] } = useQuery({
    queryKey: ['ignore-rules'],
    queryFn: async () => unwrap(await supabase.from('email_ignore_rules').select('id, rule_type, pattern, note').order('pattern')),
  });
  const add = useMutation({
    mutationFn: async () => {
      const p = pattern.trim().toLowerCase().replace(/^@/, '');
      if (!p) throw new Error('Enter a domain (rg-re.com) or an email address.');
      unwrap(await supabase.from('email_ignore_rules').insert({ pattern: p, rule_type: p.includes('@') ? 'email' : 'domain' }));
    },
    onSuccess: () => {
      setPattern('');
      qc.invalidateQueries({ queryKey: ['ignore-rules'] });
    },
  });
  const remove = useMutation({
    mutationFn: async (id: string) => unwrap(await supabase.from('email_ignore_rules').delete().eq('id', id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ignore-rules'] }),
  });
  return (
    <Card title="Email ignore list">
      <p className="mb-3 text-sm text-slate-600">Senders and domains Outlook sync (Milestone 4) will never log or suggest as contacts — your own firm, newsletters, automated mail.</p>
      <div className="mb-3 flex gap-2">
        <Input value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="rg-re.com or noreply@example.com" className="!max-w-sm" />
        <Button onClick={() => add.mutate()} busy={add.isPending}>
          Add
        </Button>
      </div>
      <ul className="space-y-1 text-sm">
        {data.map((r) => (
          <li key={r.id} className="group flex items-center gap-2">
            <span className="text-xs text-slate-400">{r.rule_type}</span> {r.pattern}
            <button className="hidden text-slate-300 hover:text-rose-600 group-hover:block" onClick={() => remove.mutate(r.id)} aria-label="Remove">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
      <ErrorBox error={add.error} />
    </Card>
  );
}
